import useTranslation from 'next-translate/useTranslation';
import { useState } from 'react';
import { Dispatch, SetStateAction, useEffect } from 'react';
import { Country } from 'typings/api/country';
import { useFetch } from '../../api/useFetch';
import { useAuth } from '../../context/authContext';
import { CardType } from '../../enums/buyType';
import { Card } from '../../typings/api';
import CardInput from '../CardInput';
import LoadingComponent from '../LoadingComponent';
import Select, { SelectOption } from '../Select/Select';
import styles from './index.module.scss';

interface Props {
  setSelected: Dispatch<SetStateAction<string>>;
  selected: string;
  showCountrySelect?: boolean;
}

const Cards = ({ setSelected, selected, showCountrySelect }: Props): JSX.Element => {
  const { t } = useTranslation();
  const { user, isUserLoaded, isAnonymous, locale } = useAuth();
  const [countryOptions, setCountryOptions] = useState<SelectOption[]>(null);
  const [preselectedCountry, setPreselectedCountry] = useState<number>(null);
  const { data: cards, isValidating } = useFetch<Card[]>('/user/cards', locale, !!user);
  const { data: countries } = useFetch<Country[]>('countries', locale, isUserLoaded && showCountrySelect);

  useEffect(() => {
    if (cards?.length > 0) {
      const defaulCard = cards.find((card) => card.is_default) ?? cards[0];
      setSelected(defaulCard.id);
    }
  }, [cards]);

  useEffect(() => {
    if (countries) {
      setCountryOptions(countries?.map((c) => ({ value: c.id, label: c.title })));
      setPreselectedCountry(countries.find((c) => c.suggested)?.id);
    }
  }, [countries]);

  const loading = <LoadingComponent />;
  if (!isUserLoaded || isValidating) {
    return loading;
  }

  if (isAnonymous && isUserLoaded) {
    return (
      <>
        <label className={['email', styles.cardLabel].join(' ')}>
          {t('common:card_email')}
          <input type="text" id="email" name="email" />
          <span></span>
        </label>
        {showCountrySelect && !!countryOptions && (
          <label className={['email', styles.cardLabel].join(' ')}>
            {t('common:country')}
            <Select
              options={countryOptions}
              defaultLabel={t('common:pick_country')}
              name="taxCountry"
              defaultValue={preselectedCountry}
            />
            <span></span>
          </label>
        )}
        <CardInput />
      </>
    );
  }

  const getLabeledCards = () => {
    return cards.map((card) => {
      return {
        label: `${card.brand[0].toUpperCase() + card.brand.slice(1)} **** ${card.last_four_digits}`,
        value: card.id,
      };
    });
  };

  return (
    <>
      {cards && cards.length > 0 ? (
        <>
          <label className={styles.cardLabel}>
            {t('common:saved_cards')}
            <Select
              defaultLabel={t('common:saved_cards_placeholder')}
              name={'card'}
              defaultValue={cards[0].id}
              onChange={setSelected}
              options={getLabeledCards().concat({ label: t('common:add_new_card'), value: CardType.NewCard })}
            />
            <span></span>
          </label>
          {selected === CardType.NewCard ? <CardInput /> : null}
        </>
      ) : (
        <CardInput />
      )}
    </>
  );
};

export default Cards;
